import {type ClassValue, clsx} from 'clsx';
import {twMerge} from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const TRUNCATION_MARKER = /\.\.\.\[truncated \d+ chars\]\s*$/;

export const isTruncated = (value?: string | null): boolean =>
  !!value && TRUNCATION_MARKER.test(value);

export const prettify = (value?: string | null): string => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return value;
  }
};

export const formatDuration = (durationMs?: number | null): string => {
  if (durationMs === null || durationMs === undefined) return '—';
  if (durationMs < 1000) return `${durationMs} ms`;
  return `${(durationMs / 1000).toFixed(2)} s`;
};

export const summarise = (step: {
  error?: string | null;
  result?: string | null;
  response?: string | null;
  input?: string | null;
}): string => {
  const raw =
    step.error ?? step.result ?? step.response ?? step.input ?? '';
  const flat = raw.replace(/\s+/g, ' ').trim();
  if (flat.length <= 120) return flat;
  return `${flat.slice(0, 120)}…`;
};

export const parseIsoDate = (str?: string | null): Date | null => {
  if (!str) return null;
  let s = String(str).trim();
  if (!s) return null;
  // If it is an ISO date-time without timezone indicator (no Z, no +HH:MM, no -HH:MM), treat as UTC
  if (s.length >= 10 && !/[Z+-]\d*(:\d+)?$/.test(s)) {
    s = s.replace(' ', 'T') + 'Z';
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) {
    const fallback = new Date(str);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }
  return d;
};

export const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

export const formatDetailedDateTime = (dateString?: string | null): string => {
  const d = parseIsoDate(dateString);
  if (!d) return dateString ? String(dateString) : '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = d.getFullYear();
  const month = pad(d.getMonth() + 1);
  const day = pad(d.getDate());
  const hours = pad(d.getHours());
  const minutes = pad(d.getMinutes());
  const seconds = pad(d.getSeconds());
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

export const formatStepTime = (dateString?: string | null): string => {
  const d = parseIsoDate(dateString);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

export const formatUtcDateTime = (dateString?: string | null): string => {
  return formatDetailedDateTime(dateString);
};
